import { apiRequest } from "@/lib/auth-api";

export type BrowserAction = "click" | "fill" | "select";

export interface BrowserElement {
    element_id: string;
    index: number;
    tag: string;
    role: string;
    name: string;
    input_type: string | null;
    disabled: boolean;
    selector: string;
}

export interface BrowserEvent {
    kind: string;
    detail: string;
    at: string;
}

export interface BrowserSession {
    session_id: string;
    status: "ACTIVE" | "AWAITING_CONFIRMATION" | "EXECUTING" | "CLOSED" | "FAILED";
    target_url: string;
    requirement: string | null;
    current_url: string;
    title: string;
    revision: number;
    screenshot_data_url: string;
    elements: BrowserElement[];
    events: BrowserEvent[];
}

export interface BrowserActionPlan {
    plan_id: string;
    session_id: string;
    action: BrowserAction;
    target: BrowserElement;
    value_summary: string | null;
    expected_revision: number;
    requires_confirmation: true;
    expires_at: string;
}

const csrfHeaders = (csrfToken: string) => ({ "X-CSRF-Token": csrfToken });

export function createBrowserSession(targetUrl: string, requirement: string, csrfToken: string): Promise<BrowserSession> {
    return apiRequest<BrowserSession>("/browser/sessions/create", {
        method: "POST",
        headers: csrfHeaders(csrfToken),
        body: JSON.stringify({ target_url: targetUrl, ...(requirement.trim() ? { requirement: requirement.trim() } : {}) }),
        signal: AbortSignal.timeout(25_000),
    });
}

export function analyzeBrowserSession(sessionId: string, csrfToken: string): Promise<BrowserSession> {
    return apiRequest<BrowserSession>("/browser/sessions/analyze", {
        method: "POST",
        headers: csrfHeaders(csrfToken),
        body: JSON.stringify({ session_id: sessionId }),
        signal: AbortSignal.timeout(25_000),
    });
}

export function prepareBrowserAction(
    session: BrowserSession,
    elementId: string,
    action: BrowserAction,
    value: string | undefined,
    csrfToken: string,
): Promise<BrowserActionPlan> {
    return apiRequest<BrowserActionPlan>("/browser/actions/prepare", {
        method: "POST",
        headers: csrfHeaders(csrfToken),
        body: JSON.stringify({
            session_id: session.session_id,
            element_id: elementId,
            action,
            ...(action === "click" ? {} : { value: value ?? "" }),
            expected_revision: session.revision,
        }),
    });
}

export function executeBrowserAction(sessionId: string, planId: string, csrfToken: string): Promise<BrowserSession> {
    return apiRequest<BrowserSession>("/browser/actions/execute", {
        method: "POST",
        headers: csrfHeaders(csrfToken),
        body: JSON.stringify({ session_id: sessionId, plan_id: planId, confirmed: true }),
        signal: AbortSignal.timeout(25_000),
    });
}

export function closeBrowserSession(sessionId: string, csrfToken: string): Promise<BrowserSession> {
    return apiRequest<BrowserSession>("/browser/sessions/close", {
        method: "POST",
        headers: csrfHeaders(csrfToken),
        body: JSON.stringify({ session_id: sessionId }),
    });
}
